import { getAmazonConnectLoginPopupConfig } from "@expert/amazon-connect-utils";
import { getAppConfigSingleton } from "@expert/app-config";
import { sendMetrics } from "@expert/cloudwatch-metrics";
import { getAmazonConnectLoginUrl, type ServiceProvider, selectPartnerWithOverride } from "@expert/service-providers";
import type { ExpertWorkspaceWebSocket } from "@expert/websocket";
import { getServiceProviderAccessToken } from "@/auth";
import { env } from "@/config";
import type { DataKeysCache } from "@/encryption";
import type { ChatEventCallbacks, ChatMessagesCache } from "@/sdk";
import type { AgentSdkBase } from "@/sdk/agent/agentSdkBase";
import { OrchestratorAgentSdk } from "@/sdk/agent/orchestrator/orchestratorAgentSdk";
import { userCache } from "@/shared-utils";

const IS_DEV = import.meta.env.VITE_ENV_TYPE === "development";

/** Provider-agnostic parameters required to create the orchestrated agent SDK. */
export interface CreateSdkParams {
    /** Function to create HTML containers for provider UIs (e.g., Amazon Connect CCP) */
    createContainer: (name: string) => Promise<HTMLElement>;
    employeeNum: string | null;
    serviceProviders: readonly ServiceProvider[];
    userFriendlyName?: string;
    /** Cache for encryption/decryption keys used in secure messaging */
    dataKeysCache: DataKeysCache;
    webSocket: ExpertWorkspaceWebSocket;
    /** Cache for storing and retrieving chat messages across providers */
    chatMessagesCache: ChatMessagesCache;
    /** Universal chat event callbacks - handles notifications, analytics, etc. */
    chatEventCallbacks: ChatEventCallbacks;
    additionalSdks?: AgentSdkBase[];
    onAuthRedirect?: VoidFunction;
    ignoreEvl?: boolean;
}

/**
 * Factory functions for creating provider-specific agent SDKs
 */
type AgentSdkFactory = {
    [key in ServiceProvider]: (createSdkParams: CreateSdkParams) => Promise<AgentSdkBase>;
};

const agentSdkFactory: AgentSdkFactory = {
    AMAZON_CONNECT: async function create({
        createContainer,
        webSocket,
        employeeNum,
        onAuthRedirect,
        ignoreEvl = false,
        chatEventCallbacks,
        chatMessagesCache,
        dataKeysCache,
    }: CreateSdkParams) {
        const { amazonConnectInstancesInfo, allowedPartners, getPartnersConfig } = getAppConfigSingleton();
        if (!amazonConnectInstancesInfo) throw new Error("amazonConnectInstancesInfo is not defined");

        // TODO: allow multi-partner configuration.
        const partner = selectPartnerWithOverride(allowedPartners[0]);
        const amazonConnectInstancesInfoConfigKey = getPartnersConfig(partner)?.amazonConnectInstancesInfoConfigKey;
        const partnerWithOrg = userCache.org ? `${partner}:${userCache.org}` : partner;

        let connectInstanceInfo = amazonConnectInstancesInfo["others"];
        if (amazonConnectInstancesInfoConfigKey && amazonConnectInstancesInfoConfigKey in amazonConnectInstancesInfo) {
            connectInstanceInfo = amazonConnectInstancesInfo[amazonConnectInstancesInfoConfigKey];
        } else if (partnerWithOrg in amazonConnectInstancesInfo) {
            connectInstanceInfo = amazonConnectInstancesInfo[partnerWithOrg];
        }

        const { instanceAlias, instanceId, region, identityProvider, isCallbackLinkingEnabled } = connectInstanceInfo;

        const contactControlPanelUrl = `https://${instanceAlias}.my.connect.aws/connect/ccp-v2`;
        const { default: AmazonConnectAgentSDK } = await import("../amazon-connect/AmazonConnectAgentSDK");

        const container = await createContainer(`Amazon Connect Contact Control Panel: ${instanceAlias}`);

        const context = {
            environment: import.meta.env.VITE_ENV_TYPE as string,
            channel: env.channel || "voice",
            employeeId: employeeNum ?? "",
        };
        const { loginPopup: loginPopupFromConfig, loginPopupAutoClose } = await getAmazonConnectLoginPopupConfig(
            "ExWo",
            context,
        );
        const isHttps = globalThis.location.protocol === "https:";
        const loginPopup = loginPopupFromConfig || !isHttps;
        const destinationRelay = loginPopup ? undefined : globalThis.location.href;
        const loginUrl =
            identityProvider && getAmazonConnectLoginUrl(identityProvider, region, instanceId, destinationRelay);

        return await AmazonConnectAgentSDK.create(container, contactControlPanelUrl, partner, employeeNum, webSocket, {
            loginUrl,
            loginPopup,
            loginPopupAutoClose,
            metricsHandler: sendMetrics,
            onAuthRedirect,
            isCallbackLinkingEnabled,
            ignoreEvl,
            connectRegion: region,
            // Only pass chat dependencies when chat channel is enabled
            ...(env.channel === "chat"
                ? {
                      chatDependencies: { chatEventCallbacks, chatMessagesCache, dataKeysCache },
                  }
                : {}),
        });
    },
    TWILIO: async ({
        employeeNum,
        userFriendlyName,
        dataKeysCache,
        webSocket,
        chatMessagesCache,
        chatEventCallbacks,
    }) => {
        const { TwilioAgentSDK } = await import("../twilio/twilioAgentSdk");

        return await TwilioAgentSDK.create(
            () => getServiceProviderAccessToken(userFriendlyName),
            employeeNum,
            dataKeysCache,
            chatMessagesCache,
            chatEventCallbacks,
            webSocket,
            userFriendlyName,
        );
    },
    MOCK_PROVIDER: async ({ employeeNum }: CreateSdkParams) => {
        if (!IS_DEV) throw new Error("MOCK_PROVIDER is only available in the development environment");
        if (!employeeNum) throw new Error("employeeNum must be provided to MOCK_PROVIDER");

        const { MockSdk } = await import("@/mock-sdk/MockSdk");

        return new MockSdk({ employeeNum });
    },
};

// TODO: remove legacy support once all feature flags have been updated in all server environments
function transformLegacyServiceProvider(serviceProvider: ServiceProvider): ServiceProvider {
    if (serviceProvider.startsWith("AMAZON_CONNECT_")) return "AMAZON_CONNECT";
    return serviceProvider;
}

/**
 * Initializes all specified service providers and combines them
 * into a single orchestrated SDK instance that supports multiple providers.
 */
export async function createAgentSDK(params: CreateSdkParams) {
    const agentSdks = await Promise.all(
        params.serviceProviders.map((serviceName) =>
            agentSdkFactory[transformLegacyServiceProvider(serviceName)](params),
        ),
    );

    const additionalSdks = params.additionalSdks ?? [];

    return new OrchestratorAgentSdk([...agentSdks, ...additionalSdks]);
}
